// Fetches the pinned milieu-circulariteit interventiebibliotheek.
//
// Same week-cache + in-flight-dedup shape as useMsiMonetaryScores, with its own
// cache key. Deliberately a separate hook from the effect-standaard fetching:
// this document has `interventies`, not `effecten`, and must never reach
// adaptMeetstandaardDocument or the effect picker.
//
// CACHES THE RAW DOCUMENT, ADAPTS ON READ. Caching the adapted library would
// mean a change to adaptInterventieDocument only took effect once every warm
// browser cache expired — a week of stale numbers. That is exactly why the MSI
// score cache key had to be bumped to V2; not repeating it here.
//
// THE CACHE KEY IS PART OF THE CONTRACT WITH THE DOCUMENT. Bumped to V2 on
// 2026-08-17: the api republished milieu-circulariteit 0.9 *in place* (95 → 114
// interventies, `berekend` fields renamed), so a warm cache holds a body that no
// longer matches what the same URL serves. The week-long TTL would have kept
// those stale numbers on screen; the new key drops them on first load instead.
// Bump it again whenever the document behind a pinned version changes.
//
// FAILS VISIBLY. On any error the library is null and `error` is set: callers
// must skip the calculation entirely rather than compute against a partial
// library, in which a missing interventie is indistinguishable from one the
// standaard cannot quantify — both would read as "no CO2".
//
// AI-CONTEXT: See docs/features/materials-co2-circulariteit.md

import { useEffect, useState } from 'react'
import {
    PINNED_INTERVENTIE_VERSIONS,
    interventieDocumentUrl,
} from '../helpers/meetstandaard/interventieVersions'
import adaptInterventieDocument from '../helpers/meetstandaard/adaptInterventieDocument'

const INTERVENTIE_CACHE_KEY = 'interventieLibraryCacheV2'
const WEEK_MS = 7 * 24 * 60 * 60 * 1000

// Only one interventiebibliotheek exists today. Kept as a lookup rather than a
// literal so adding a second standaard is a change to the pin map alone.
const STANDAARD = 'milieu-circulariteit'

const loadCached = () => {
    try {
        const raw = localStorage.getItem(INTERVENTIE_CACHE_KEY)
        if (!raw) return null
        const parsed = JSON.parse(raw)
        if (!parsed?.data || Date.now() - parsed.fetchedAt > WEEK_MS) return null
        // The key is the same for every version, so after a pin bump a warm
        // browser would keep serving the previous publication for up to a week.
        // A cached body only counts if it is the version we pin.
        if (parsed.data.meta?.version !== PINNED_INTERVENTIE_VERSIONS[STANDAARD]) return null
        return parsed.data
    } catch (e) {
        console.warn('Failed to parse interventie library cache', e)
        return null
    }
}

const saveCached = (data) => {
    try {
        localStorage.setItem(INTERVENTIE_CACHE_KEY, JSON.stringify({ data, fetchedAt: Date.now() }))
    } catch (e) {
        console.warn('Failed to save interventie library cache', e)
    }
}

// On a cold cache every card mounts at once; share one request rather than
// firing one per instance. Cleared afterwards so a failure can be retried.
let pendingFetch = null

const fetchDocumentShared = () => {
    if (!pendingFetch) {
        pendingFetch = (async () => {
            const url = interventieDocumentUrl(STANDAARD, PINNED_INTERVENTIE_VERSIONS[STANDAARD])
            const res = await fetch(url)
            if (!res.ok) throw new Error(`Meetstandaard ${STANDAARD} gaf status ${res.status}`)
            const document = await res.json()
            // Adapt before caching purely as validation — the wrong document
            // kind must fail now, not on every later read from cache.
            adaptInterventieDocument(document)
            saveCached(document)
            return document
        })().finally(() => { pendingFetch = null })
    }
    return pendingFetch
}

/**
 * @typedef {object} InterventieLibraryState
 * @property {import('../helpers/meetstandaard/adaptInterventieDocument').AdaptedInterventieLibrary|null} library
 * @property {boolean} loading
 * @property {string|null} error
 */

/**
 * @returns {InterventieLibraryState}
 */
const useInterventieLibrary = () => {
    const [library, setLibrary] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)

    useEffect(() => {
        let cancelled = false

        const settle = (document) => {
            if (cancelled) return
            try {
                setLibrary(adaptInterventieDocument(document))
                setError(null)
            } catch (e) {
                setLibrary(null)
                setError(e.message)
            }
            setLoading(false)
        }

        const cached = loadCached()
        if (cached) {
            settle(cached)
            return () => { cancelled = true }
        }

        fetchDocumentShared()
            .then(settle)
            .catch((e) => {
                if (cancelled) return
                console.error('useInterventieLibrary fetch error', e)
                setLibrary(null)
                setError(e.message || 'Kon de interventiebibliotheek niet ophalen')
                setLoading(false)
            })

        return () => { cancelled = true }
    }, [])

    return { library, loading, error }
}

export default useInterventieLibrary
export { INTERVENTIE_CACHE_KEY }
